import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { AdControls } from '@post-office/components--ad-controls';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import type { ClickedElementAttributes } from '@post-office/message-template-contracts/message-instance-event';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { FormattedMessage } from 'react-intl';
import { useEffect, useMemo } from 'react';

import {
	getEnvTypeFromHostname,
	getRovoOnboardingClickUrl,
} from '../../../../../common/rovo-onboarding-url';
import { registeredContent } from '../../../../../common/content';
import { ROVO_ICON } from '../../assets/rovo-icon';
import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	root: {
		position: 'relative',
		flexGrow: 1,
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
	},
	primaryContentContainer: {
		flexGrow: 1,
		padding: token('space.200'),
	},
	headingContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	iconContainer: {
		flexShrink: 0,
		width: '32px',
		height: '32px',
	},
	ctaWrapper: {
		marginTop: token('space.050'),
	},
	adControlsContainer: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});

type RovoOnboardingBannerMessageInstanceClickedParams = {
	actionType: 'open' | 'dismiss';
	clickedElement: ClickedElementAttributes;
};

export type RovoOnboardingBannerProps = {
	action: NextBestAction;
	/** Org ID for the Rovo admin URL; set by implicit message creator (from context or TCS). Optional when triggering manually. */
	orgId?: string;
	onPrimaryActionClick?: (nextBestActionType: NextBestAction) => string | void | null;
	onDismissClick?: () => void;
};

export const RovoOnboardingBanner = ({
	action,
	orgId: orgIdFromMessage,
	onPrimaryActionClick,
	onDismissClick,
}: RovoOnboardingBannerProps): JSX.Element => {
	const postOfficeContext = usePostOfficeContext();
	// Use orgId from message context first; fallback to product/placement context (e.g. when message payload omits it but event context has it)
	const orgId =
		orgIdFromMessage ??
		(typeof postOfficeContext?.orgId === 'string' ? postOfficeContext.orgId : undefined);

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (!messageInstanceViewed) {
			return;
		}

		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'ConfluenceRovoOnboardingBanner',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fireMessageInstanceClicked = ({
		actionType,
		clickedElement,
	}: RovoOnboardingBannerMessageInstanceClickedParams) => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: clickedElement,
			action: {
				actionType: actionType,
			},
			additionalMetadataFromFrontend: {},
		});
	};

	const rovoAdminUrl = useMemo(() => {
		if (!globalThis.location) {
			return '#';
		}
		const envType = getEnvTypeFromHostname(globalThis.location.hostname);
		return getRovoOnboardingClickUrl(orgId, envType);
	}, [orgId]);

	const handleOnPrimaryActionClick = () => {
		void fireMessageInstanceClicked({
			actionType: 'open',
			clickedElement: {
				clickIdentifier: 'getStarted',
				clickedElement: 'button',
				buttonType: 'primary',
			},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoOnboardingBannerCta',
		});

		void onPrimaryActionClick?.(action);

		if (rovoAdminUrl && rovoAdminUrl !== '#' && globalThis.location) {
			globalThis.location.href = rovoAdminUrl;
		}
	};

	const handleOnDismissed = (_dismissedMessageInstanceId: string) => {
		void onDismissClick?.();
	};

	return (
		<AnalyticsContext data={{ source: 'confluence-rovo-onboarding-banner' }}>
			<Flex xcss={styles.root} testId="confluence-rovo-onboarding-banner">
				<Flex xcss={styles.primaryContentContainer} gap="space.100" direction="column">
					<Flex xcss={styles.headingContainer}>
						<Box xcss={styles.iconContainer}>
							<ROVO_ICON />
						</Box>
						<Heading size="small">
							<FormattedMessage {...registeredContent.default.headingLabel} />
						</Heading>
					</Flex>
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...registeredContent.default.bodyLabel} />
					</Text>
					<Box xcss={styles.ctaWrapper}>
						<LinkButton
							href={rovoAdminUrl}
							onClick={handleOnPrimaryActionClick}
							spacing="compact"
							target="_self"
						>
							<FormattedMessage {...registeredContent.default.callToActionLabel} />
						</LinkButton>
					</Box>
				</Flex>
				<Box xcss={styles.adControlsContainer}>
					<AdControls
						iconSize="small"
						spacing="compact"
						onDismissed={handleOnDismissed}
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							message: (
								<FormattedMessage {...registeredContent.default.whyAmISeeingThisModalMessage} />
							),
							reasons: [
								<FormattedMessage
									key="reason"
									{...registeredContent.default.whyAmISeeingThisModalReason}
								/>,
							],
						}}
					/>
				</Box>
			</Flex>
		</AnalyticsContext>
	);
};
